import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Row } from "react-table";
import DefaultColumnFilter from '../datatable.columnfilter';
import { ColumnProps, defaultColumnProps } from '../ColumnProps';

/**
 * @param header Translation key for header of the column
 * @param identifier Uniquely identifies each row in the table. This should be the same as all other editable columns within the same table, e.g. an objects Id
 * @param accessor The object's field that is being used for the column
 * @param options JSX Element of options for the select control
 * @param canEdit Enables or disabled input field
 * @param filterMethod
 * @param filterComponent
 * @returns React-Table column definition for a column that has inputs
 */
function EditableSelectColumn<T extends object>(
    header: string,
    accessor: keyof T,
    options: any[],
    canEdit: boolean,
    filterMethod: string = 'includesString',
    filterComponent?: Function,
    props: ColumnProps = defaultColumnProps) {
    const { t } = useTranslation();

    return {
        Header: t<string>(header),
        id: header,
        accessor: accessor.toString(),
        Cell: (props: any) => <EditableCell {...props} accessor={accessor} canEdit={canEdit} options={options} />,
        filter: filterMethod,
        Filter: filterComponent ?? DefaultColumnFilter,
        ...props,
    };
}

interface EditableCellProps<T extends object> {
    row: Row<T>,
    accessor: keyof T,
    value: any,
    options: any[],
    canEdit: boolean,
    updateData: (accessor: keyof T, row: Row<T>, value: any) => void,
}

function EditableCell<T extends object>({ row, accessor, value: initialValue, options, canEdit, updateData }: EditableCellProps<T>) {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        updateData(accessor, row, value);
    }, [accessor, value]);

    const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        e.preventDefault();
        setValue(e.target.value);
    }

    if (!canEdit) {
        return (
            <Form.Control
                key={`table-${row.index}-${accessor}`}
                value={value} 
                onChange={(e) => setValue(e.target.value)}
                readOnly={true}
                plaintext={true} />
        );
    }

    // If the current value is not in the options list, add it as an option so it can be displayed and selected
    if (!options.some(option => option.value === value)) {
        options = [...options, { value: initialValue, display: initialValue }];
    }

    return (
        <Form.Select
            key={`table-${row.index}-${accessor}`}
            value={value}
            onChange={onChange}>
            {options.map((option: any, index: number) =>(
                <option key={`table-${row.index}-${accessor}-option-${index}`} value={option.value}>
                    {option.display}
                </option>))}
        </Form.Select>
    );
}

export default EditableSelectColumn;